class HzEmailPopup extends HTMLElement {
  #dialog;
  #timeout;
  #exitHandler;

  connectedCallback() {
    this.#dialog = this.querySelector('[data-popup-dialog]');
    if (!this.#dialog) return;

    this.querySelectorAll('[data-popup-close]').forEach((button) => {
      button.addEventListener('click', this.#close);
    });
    this.#dialog.addEventListener('click', this.#handleDialogClick);
    this.#dialog.addEventListener('cancel', this.#handleCancel);

    const hasSuccess = Boolean(this.querySelector('[data-popup-success]'));
    if (hasSuccess) {
      this.#remember(true);
      this.#open(true);
      return;
    }

    if (this.#suppressed()) return;

    const delay = Math.max(0, Number(this.dataset.delaySeconds) || 0);
    this.#timeout = window.setTimeout(() => this.#open(), delay * 1000);

    if (this.dataset.showOnExitIntent === 'true') {
      this.#exitHandler = (event) => {
        if (event.clientY <= 0) this.#open();
      };
      document.addEventListener('mouseout', this.#exitHandler);
    }
  }

  disconnectedCallback() {
    if (this.#timeout) window.clearTimeout(this.#timeout);
    if (this.#exitHandler) document.removeEventListener('mouseout', this.#exitHandler);
    this.querySelectorAll('[data-popup-close]').forEach((button) => {
      button.removeEventListener('click', this.#close);
    });
    this.#dialog?.removeEventListener('click', this.#handleDialogClick);
    this.#dialog?.removeEventListener('cancel', this.#handleCancel);
    this.#timeout = undefined;
    this.#exitHandler = undefined;
    this.#dialog = undefined;
  }

  #open = (force = false) => {
    if (!this.#dialog || (!force && this.#suppressed())) return;
    if (this.#dialog.showModal) {
      if (!this.#dialog.open) this.#dialog.showModal();
    } else {
      this.#dialog.setAttribute('open', '');
    }
    this.#stopTriggers();
    this.#remember();
  };

  #close = () => {
    if (!this.#dialog) return;
    if (this.#dialog.close) this.#dialog.close();
    else this.#dialog.removeAttribute('open');
    this.#stopTriggers();
    this.#remember();
  };

  #handleDialogClick = (event) => {
    if (event.target === this.#dialog) this.#close();
  };

  #handleCancel = (event) => {
    event.preventDefault();
    this.#close();
  };

  #suppressed() {
    try {
      const storageKey = this.dataset.storageKey || 'hz-custom:email-popup:v1';
      const lastSeen = Number(window.localStorage.getItem(storageKey) || 0);
      const lastSuccess = Number(window.localStorage.getItem(`${storageKey}:success`) || 0);
      const frequencyDays = Math.max(0, Number(this.dataset.frequencyDays) || 0);
      const successFrequencyDays = Math.max(0, Number(this.dataset.successFrequencyDays) || 0);
      const now = Date.now();
      return (
        (lastSeen > 0 && now < lastSeen + frequencyDays * 86400000) ||
        (lastSuccess > 0 && now < lastSuccess + successFrequencyDays * 86400000)
      );
    } catch {
      return false;
    }
  }

  #remember(success = false) {
    try {
      const storageKey = this.dataset.storageKey || 'hz-custom:email-popup:v1';
      const now = String(Date.now());
      window.localStorage.setItem(storageKey, now);
      if (success) window.localStorage.setItem(`${storageKey}:success`, now);
    } catch {
      // Private browsing or disabled storage should not block the popup.
    }
  }

  #stopTriggers() {
    if (this.#timeout) window.clearTimeout(this.#timeout);
    if (this.#exitHandler) document.removeEventListener('mouseout', this.#exitHandler);
    this.#timeout = undefined;
    this.#exitHandler = undefined;
  }
}

if (!customElements.get('hz-email-popup')) {
  customElements.define('hz-email-popup', HzEmailPopup);
}
